(ns hai.notes.stacks
  "The dashboard scratchpad — a per-user singleton quick note (`:note/scratchpad?
   true`, `:note/source :web-app`). Its text auto-saves; tags, links, and starting
   a fresh note have explicit controls. Starting a fresh note archives the current
   one (clears the scratchpad flag) so it lands in the notes wall, then remounts
   the pad blank. Shared by the desktop dashboard and the mobile quick-note card."
  (:require
   [clojure.string :as str]
   [hai.entity.data :as entity-data]
   [hai.entity.staging :as staging]
   [hai.notes.editor :as editor]
   [hai.notes.eql :as eql]
   [hai.notes.mutations :as mutations]
   [hai.sharing.helper :as sharing]
   [hai.ui.components.form.core :as form]
   [hai.ui.rf-sugar :refer [>e defevent defevent-fx defsub]]))

(def ^:private chips-scope
  "This host's staging scope ([[hai.entity.staging]]) — a session key rather
   than the note's uuid, which a fresh pad doesn't have yet."
  ::chips)

(defevent-fx ::load [{:keys [db]} source]
  {:db (-> db
           (assoc ::source source ::loaded? false)
           (dissoc ::note))
   :resolve {:query [{:user/home-scratchpad eql/note-eql}]
             :store-in [::note]
             :transform-fn (fn [resp] (get resp :user/home-scratchpad))
             :on-success-fx [[:dispatch [::loaded]]]}})

(defevent ::loaded [db _]
  (assoc db ::loaded? true))

;; ::load minus the ::loaded? round-trip: re-resolves the note in place while
;; views stay mounted (e.g. after the sharing modal closes).
(defevent-fx ::refresh [_ _]
  {:resolve {:query [{:user/home-scratchpad eql/note-eql}]
             :transform-fn (fn [resp] (get resp :user/home-scratchpad))
             :store-in [::note]}})

(defevent ::clear [db _]
  (-> db
      (dissoc ::note ::loaded? ::source ::saving? ::generation ::save-request-id)
      (staging/clear chips-scope)))

(defevent-fx ::save
  [{:keys [db]} {:keys [staged removal] :as payload}]
  ;; `:staged` — `{:family … :chip … :error-text …}` — is an optimistic chip
  ;; shown while this save is in flight; `:removal` — `{:family … :id …
  ;; :error-text …}` — one hidden while its detach is. Both are retired when
  ;; the response (which then carries the change) lands or fails.
  (let [note-uuid (get-in db [::note :note/uuid])
        source (get db ::source :web-app)]
    (cond
      ;; Nothing to create from yet.
      (and (nil? note-uuid) (mutations/empty-payload? payload))
      {:db db}

      :else
      (let [req-id (random-uuid)
            params (cond-> (mutations/upsert-params {:note-uuid note-uuid :source source}
                                                    payload)
                     ;; First save creates the active scratchpad; later updates
                     ;; leave the flag untouched.
                     (nil? note-uuid) (assoc :note/scratchpad? true))]
        {:db (-> db
                 (assoc ::saving? true ::save-request-id req-id)
                 (cond-> staged (staging/stage chips-scope (:family staged) (:chip staged)))
                 (cond-> removal (staging/mark-removal chips-scope (:family removal) (:id removal))))
         :resolve (assoc (mutations/upsert-resolve params)
                         :request-id req-id
                         :store-in [::note]
                         :on-success-fx (cond-> [[:dispatch [::save-success]]]
                                          staged (conj [:dispatch [::staging/unstage chips-scope
                                                                   (:family staged) (:chip staged)]])
                                          removal (conj [:dispatch [::staging/unmark-removal chips-scope
                                                                    (:family removal) (:id removal)]]))
                         :on-error-fx (cond-> [[:dispatch [::save-error]]]
                                        staged (conj [:dispatch [::staging/add-failed chips-scope
                                                                 (:family staged) (:chip staged)
                                                                 (:error-text staged)]])
                                        removal (conj [:dispatch [::staging/remove-failed chips-scope
                                                                  (:family removal) (:id removal)
                                                                  (:error-text removal)]])))}))))

;; Removals are optimistic through [[hai.entity.staging]] marks, like the adds
;; riding [[::save]]: the chip hides immediately, the response reconciles via
;; store-in, and an error unhides it with a toast. Marks survive an in-flight
;; autosave's whole-note store-in, which the snapshot restores here previously
;; didn't.
(defn- marked-removal
  [db {:keys [family id error-text resolve]}]
  {:db (staging/mark-removal db chips-scope family id)
   :resolve (assoc resolve
                   :store-in [::note]
                   :on-success-fx [[:dispatch [::staging/unmark-removal chips-scope family id]]]
                   :on-error-fx [[:dispatch [::staging/remove-failed chips-scope family id error-text]]])})

(defevent-fx ::remove-link
  [{:keys [db]} link-url]
  (when-let [note-uuid (get-in db [::note :note/uuid])]
    (marked-removal db {:family :links
                        :id link-url
                        :error-text "Could not remove link. Try again."
                        :resolve (mutations/remove-link-resolve note-uuid link-url)})))

(defevent-fx ::remove-contact
  [{:keys [db]} contact-uuid]
  (when-let [note-uuid (get-in db [::note :note/uuid])]
    (marked-removal db {:family :contacts
                        :id contact-uuid
                        :error-text "Could not remove contact. Try again."
                        :resolve (mutations/remove-contact-resolve note-uuid contact-uuid)})))

(defevent-fx ::remove-note-link
  [{:keys [db]} linked-uuid]
  (when-let [note-uuid (get-in db [::note :note/uuid])]
    (marked-removal db {:family :notes
                        :id linked-uuid
                        :error-text "Could not remove linked note. Try again."
                        :resolve (mutations/remove-note-resolve note-uuid linked-uuid)})))

(defevent-fx ::remove-task
  [{:keys [db]} task-uuid]
  (when-let [note-uuid (get-in db [::note :note/uuid])]
    (marked-removal db {:family :tasks
                        :id task-uuid
                        :error-text "Could not remove linked task. Try again."
                        :resolve (mutations/remove-task-resolve note-uuid task-uuid)})))

(defevent ::save-success [db _]
  (-> db
      (assoc ::saving? false)
      (dissoc ::save-request-id)))

(defevent ::save-error [db _]
  (-> db
      (assoc ::saving? false)
      (dissoc ::save-request-id)))

(defn scratchpad-form-id
  "The scratchpad's single form state, shared by the inline pad and the
   full-screen editor so switching between them hands the live text over
   directly. Keyed by generation: ＋ bumps it, giving the fresh pad a blank
   form."
  [generation]
  [::form generation])

(defevent-fx ::new-note
  [{:keys [db]} _]
  (let [note (get db ::note)
        note-uuid (:note/uuid note)
        source (get db ::source :web-app)
        ;; The note entity in db lags the textarea by the autosave debounce plus
        ;; a round-trip; the form state is updated per keystroke, so it has the
        ;; text the user actually sees.
        form-id (scratchpad-form-id (get db ::generation 0))
        text (or (form/->field-current-value db form-id :note/text)
                 (:note/text note)
                 "")]
    (cond
      ;; Already on a fresh pad — nothing to archive.
      (and (nil? note-uuid) (str/blank? text))
      {:db db}

      ;; A read-only shared note landed on the pad. It isn't ours to archive —
      ;; the save would be rejected for lack of write access — so just peel it
      ;; off (it stays in the wall) and remount a blank pad. Matches the peel in
      ;; ::new-note-success, without the server round-trip.
      (and note-uuid (not (sharing/can-edit? note)))
      {:db (-> db
               (dissoc ::note)
               (update ::generation (fnil inc 0)))}

      :else
      (let [;; A debounced auto-save may be in flight for this note. Cancel it so
            ;; its response can't re-store the note as the scratchpad after we've
            ;; peeled. We file the same text here, so nothing is lost.
            pending-save-id (::save-request-id db)
            ;; Archive the current note: save its text and clear the scratchpad
            ;; flag. With no note yet it creates one already archived.
            params (cond-> {:note/text text
                            :note/source source
                            :note/scratchpad? false}
                     note-uuid (assoc :note/uuid note-uuid))]
        ;; Optimistic peel: forget the note as the scratchpad and remount the
        ;; editor blank now (the request already carries its text); the note
        ;; lives on, now in the wall. An error reloads the pad with a toast.
        {:db (cond-> (-> db
                         (dissoc ::note ::save-request-id)
                         (staging/clear chips-scope)
                         (update ::generation (fnil inc 0)))
               pending-save-id (update :resolve/canceled-requests (fnil conj #{}) pending-save-id))
         :resolve (assoc (mutations/upsert-resolve params [:note/uuid])
                         :on-error-fx [[:dispatch [::archive-failed]]])}))))

(defevent-fx ::archive-failed [_ _]
  {:fx [[:dispatch [:toast/error {:text "Could not archive note. Try again."}]]
        [:dispatch [::refresh]]]})

(defevent-fx ::delete
  [{:keys [db]} _]
  (when-let [note-uuid (get-in db [::note :note/uuid])]
    ;; Optimistic: forget the note and remount the editor blank now, the same
    ;; landing as starting a fresh note; an error reloads the pad (the note
    ;; still exists) with a toast. Cancel any in-flight autosave so its
    ;; response can't re-store the note as the scratchpad.
    (let [pending-save-id (::save-request-id db)]
      {:db (cond-> (-> db
                       (dissoc ::note ::save-request-id)
                       (staging/clear chips-scope)
                       (update ::generation (fnil inc 0)))
             pending-save-id (update :resolve/canceled-requests (fnil conj #{}) pending-save-id))
       :resolve (assoc (mutations/delete-resolve note-uuid)
                       :on-error-fx [[:dispatch [::delete-failed]]])})))

(defevent-fx ::delete-failed [_ _]
  {:fx [[:dispatch [:toast/error {:text "Could not delete note. Try again."}]]
        [:dispatch [::refresh]]]})

(defsub ::loaded? [db]
  (get db ::loaded? false))

(defsub ::note [db]
  (get db ::note))

(defsub ::link-urls [db]
  (-> (->> (get-in db [::note :note/activities])
           (keep :activity/url))
      (staging/merge-staged identity
                            (staging/staged db chips-scope :links)
                            (staging/removals db chips-scope :links))))

(defsub ::tags [db]
  (-> (get-in db [::note :note/tags] [])
      (staging/merge-staged-tags (staging/staged db chips-scope :tags)
                                 (staging/removals db chips-scope :tags))))

(defsub ::contacts [db]
  (-> (get-in db [::note :note/contacts] [])
      (staging/merge-staged :contact/uuid
                            (staging/staged db chips-scope :contacts)
                            (staging/removals db chips-scope :contacts))))

(defsub ::linked-notes [db]
  (-> (get-in db [::note :note/notes] [])
      (staging/merge-staged :note/uuid
                            (staging/staged db chips-scope :notes)
                            (staging/removals db chips-scope :notes))))

(defsub ::linked-tasks [db]
  (-> (get-in db [::note :note/tasks] [])
      (staging/merge-staged :task/uuid
                            (staging/staged db chips-scope :tasks)
                            (staging/removals db chips-scope :tasks))))

(defsub ::saving? [db]
  (get db ::saving? false))

(defsub ::generation [db]
  (get db ::generation 0))

(def ^:private debounce-ms 750)

(def extras-handlers
  "The scratchpad's tag/link save wiring for [[hai.notes.form/note-extras]] —
   hosts merge their display props (`:editing?`, `:tags`, `:link-urls`, …) on
   top. Adds ride [[::save]] with a staged optimistic chip."
  {:on-add-tag     (fn [tag]
                     (>e [::save (assoc (editor/tag-save-payload tag)
                                        :staged {:family :tags
                                                 :chip (staging/tag-chip tag)
                                                 :error-text "Could not add tag. Try again."})]))
   :on-remove-tag  (fn [tag]
                     (if-let [tag-uuid (:group/uuid tag)]
                       (>e [::save {:untag-uuids [tag-uuid]
                                    :removal {:family :tags
                                              :id tag-uuid
                                              :error-text "Could not remove tag. Try again."}}])
                       ;; ✕ on a chip still awaiting its create — retire it
                       ;; locally; the create's response restores server truth.
                       (>e [::staging/unstage chips-scope :tags tag])))
   :on-add-link    (fn [link-url]
                     (>e [::save {:link-urls [link-url]
                                  :staged {:family :links
                                           :chip link-url
                                           :error-text "Could not add link. Try again."}}]))
   :on-remove-link (fn [link-url] (>e [::remove-link link-url]))
   :on-add-contact    (fn [contact]
                        (>e [::save {:contact-uuids [(:contact/uuid contact)]
                                     :staged {:family :contacts
                                              :chip (staging/contact-chip contact)
                                              :error-text "Could not add contact. Try again."}}]))
   :on-remove-contact (fn [contact-uuid] (>e [::remove-contact contact-uuid]))
   :on-add-note       (fn [linked-note]
                        (>e [::save {:link-note-uuids [(:note/uuid linked-note)]
                                     :staged {:family :notes
                                              :chip (staging/note-chip linked-note)
                                              :error-text "Could not add linked note. Try again."}}]))
   :on-remove-note    (fn [linked-uuid] (>e [::remove-note-link linked-uuid]))
   :on-add-task       (fn [task]
                        (>e [::save {:task-uuids [(:task/uuid task)]
                                     :staged {:family :tasks
                                              :chip (staging/task-chip task)
                                              :error-text "Could not add linked task. Try again."}}]))
   :on-remove-task    (fn [task-uuid] (>e [::remove-task task-uuid]))
   :on-open-entity    entity-data/open-entity})

(defn delete-note!
  "Drop the pending autosave so the remount flush can't re-create the note
   being deleted, then delete it."
  [!autosave]
  (when-let [{:keys [discard!]} @!autosave]
    (discard!))
  (>e [::delete]))

(defn autosave
  "Debounced scratchpad save bound to the `text` ratom. Returns the handlers the
   editor wires up: `save!` cancels any pending debounce then upserts the current
   text merged with `extra`; `schedule!` debounces a text-only save; `cancel!`
   clears a pending save (also the unmount cleanup)."
  [text]
  (let [timer   (atom nil)
        cancel! (fn []
                  (when-let [id @timer]
                    (js/clearTimeout id)
                    (reset! timer nil)))
        save!   (fn [extra]
                  (cancel!)
                  (>e [::save (merge {:text @text} extra)]))]
    {:cancel!   cancel!
     :save!     save!
     :flush!    #(save! {})
     :schedule! (fn []
                  (cancel!)
                  (reset! timer (js/setTimeout #(save! {}) debounce-ms)))}))
